/**
 * File: notificationsService.ts
 * Path: apps/admin/src/shared/services/notificationsService.ts
 * Description: Every Firestore read and write for notifications: the
 * person's own switches, their devices and their bell. Live listeners, so a
 * device's confirmation shows up the moment it arrives. Each person can only
 * reach their own (firestore.rules); devices and bell entries are written by
 * the server.
 */

import {
  db,
  NOTIFICATION_COLLECTIONS,
  NOTIFICATION_INBOX_LIMIT,
  toNotificationItem,
  toNotificationSettings,
  toPushDevice,
  type NotificationEvent,
  type NotificationItem,
  type NotificationSettings,
  type PushDevice,
} from "@idk/config";
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  writeBatch,
  type Firestore,
  type Unsubscribe,
} from "firebase/firestore";

const C = NOTIFICATION_COLLECTIONS;

export const createNotificationsRepo = (getDb: () => Firestore) => {
  const settingsRef = (uid: string) => doc(getDb(), C.settings, uid);
  const devicesCol = (uid: string) => collection(getDb(), C.devices, uid, C.deviceList);
  const inboxCol = (uid: string) => collection(getDb(), C.inbox, uid, C.inboxItems);

  return {
    watchSettings: (uid: string, next: (s: NotificationSettings) => void, fail: (e: Error) => void): Unsubscribe =>
      onSnapshot(settingsRef(uid), (snap) => next(toNotificationSettings(snap.data())), fail),

    /** One switch. Merged, so the other switches are untouched. */
    setEvent: (uid: string, event: NotificationEvent, on: boolean): Promise<void> =>
      setDoc(settingsRef(uid), { events: { [event]: on }, updatedAt: Date.now() }, { merge: true }),

    /** All notifications on or off for this person (devices stay listed). */
    setEnabled: (uid: string, enabled: boolean): Promise<void> =>
      setDoc(settingsRef(uid), { enabled, updatedAt: Date.now() }, { merge: true }),

    watchDevices: (uid: string, next: (d: PushDevice[]) => void, fail: (e: Error) => void): Unsubscribe =>
      onSnapshot(
        devicesCol(uid),
        (snap) =>
          next(
            snap.docs
              .map((d) => toPushDevice(d.id, d.data()))
              .sort((a, b) => b.lastSeenAt - a.lastSeenAt),
          ),
        fail,
      ),

    removeDevice: (uid: string, deviceId: string): Promise<void> => deleteDoc(doc(devicesCol(uid), deviceId)),

    watchInbox: (uid: string, next: (items: NotificationItem[]) => void, fail: (e: Error) => void): Unsubscribe =>
      onSnapshot(
        query(inboxCol(uid), orderBy("createdAt", "desc"), limit(NOTIFICATION_INBOX_LIMIT)),
        (snap) => next(snap.docs.map((d) => toNotificationItem(d.id, d.data()))),
        fail,
      ),

    watchItem: (uid: string, id: string, next: (item: NotificationItem | null) => void): Unsubscribe =>
      onSnapshot(doc(inboxCol(uid), id), (snap) => next(snap.exists() ? toNotificationItem(snap.id, snap.data()) : null)),

    markRead: async (uid: string, ids: string[], now = Date.now()): Promise<void> => {
      if (ids.length === 0) return;
      if (ids.length === 1) {
        await updateDoc(doc(inboxCol(uid), ids[0]), { readAt: now });
        return;
      }
      const batch = writeBatch(getDb());
      for (const id of ids) batch.update(doc(inboxCol(uid), id), { readAt: now });
      await batch.commit();
    },
  };
};

export const notificationsRepo = createNotificationsRepo(() => db);

export type NotificationsRepo = ReturnType<typeof createNotificationsRepo>;
